import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface CommunicationTemplate {
  _id: string;
  name: string;
  body: string;
  subject?: string;
  template_type: 'sms' | 'email' | 'whatsapp';
  channel: string;
  variables: string[];
  character_count: number;
  usage_count: number;
  user_id: string;
  company_id: string;
  company?: {
    id: string;
    name: string;
  };
  last_used_at?: Date;
  created_at: Date;
  updated_at: Date;
}

export interface TemplateResponse {
  success: boolean;
  message: string;
  data: CommunicationTemplate[] | CommunicationTemplate;
}

@Injectable({
  providedIn: 'root'
})
export class CommunicationTemplateService {
  private baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  getTemplates(templateType?: string): Observable<TemplateResponse> {
    let params = new HttpParams();
    if (templateType) {
      params = params.set('template_type', templateType);
    }
    return this.http.get<TemplateResponse>(`${this.baseUrl}/api/templates`, { params });
  }

  getTemplatesByChannel(channel?: string): Observable<TemplateResponse> {
    let params = new HttpParams();
    if (channel) {
      params = params.set('channel', channel);
    }
    return this.http.get<TemplateResponse>(`${this.baseUrl}/api/templates`, { params });
  }

  getTemplateById(id: string): Observable<TemplateResponse> {
    return this.http.get<TemplateResponse>(`${this.baseUrl}/api/templates/${id}`);
  }

  createTemplate(template: Partial<CommunicationTemplate>): Observable<TemplateResponse> {
    return this.http.post<TemplateResponse>(`${this.baseUrl}/api/templates`, template);
  }

  updateTemplate(id: string, template: Partial<CommunicationTemplate>): Observable<TemplateResponse> {
    return this.http.put<TemplateResponse>(`${this.baseUrl}/api/templates/${id}`, template);
  }

  deleteTemplate(id: string): Observable<TemplateResponse> {
    return this.http.delete<TemplateResponse>(`${this.baseUrl}/api/templates/${id}`);
  }

  searchTemplates(query: string): Observable<TemplateResponse> {
    const params = new HttpParams().set('q', query);
    return this.http.get<TemplateResponse>(`${this.baseUrl}/api/templates/search`, { params });
  }

  useTemplate(id: string): Observable<TemplateResponse> {
    return this.http.post<TemplateResponse>(`${this.baseUrl}/api/templates/${id}/use`, {});
  }

  extractVariables(body: string): string[] {
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const variables: string[] = [];
    let match;

    while ((match = variableRegex.exec(body)) !== null) {
      const variable = match[1].trim();
      if (!variables.includes(variable)) {
        variables.push(variable);
      }
    }

    return variables;
  }

  replaceVariables(body: string, variables: { [key: string]: string }): string {
    let result = body;
    Object.keys(variables).forEach(key => {
      const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g');
      result = result.replace(regex, variables[key] || `{{${key}}}`);
    });
    return result;
  }
}